import type { FC } from 'react';
import React, { useState, useCallback, createContext, useContext, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	SPACE_DIRECTORY_FILTER_EXPERIENCE,
	ExperienceTrackerContext,
	ExperienceTimeout,
} from '@confluence/experience-tracker';
import { RoutesContext, getSingleParam } from '@confluence/route-manager';

import type { Filters } from '../types';

type ParsedUrlQuery = { [key: string]: string | string[] | undefined };

type FiltersContext = {
	filters: Filters;
	updateSearch: (text: string) => void;
	updateSpaceType: (spaceType: string) => void;
	updateLabel: (label?: string) => void;
};

const defaultFilters = {
	search: '',
	spaceType: 'all',
	label: '',
};

export const FiltersContext = createContext<FiltersContext>({
	filters: defaultFilters,
	updateSearch: () => {},
	updateSpaceType: () => {},
	updateLabel: () => {},
});

const queryParamsToFilters = (queryParams: ParsedUrlQuery): Filters => ({
	search: getSingleParam(queryParams, 'search') ?? defaultFilters.search,
	spaceType: getSingleParam(queryParams, 'spaceType') || defaultFilters.spaceType,
	label: getSingleParam(queryParams, 'label') ?? defaultFilters.label,
});

const filtersToQueryParams = (filters: Filters): Partial<Filters> => ({
	search: filters.search === defaultFilters.search ? undefined : filters.search,
	spaceType: filters.spaceType === defaultFilters.spaceType ? undefined : filters.spaceType,
	label: filters.label === defaultFilters.label ? undefined : filters.label,
});

export const FiltersContextProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
	const { getQueryParams, setQueryParams } = useContext(RoutesContext);
	const [filters, setFilters] = useState<Filters>(queryParamsToFilters(getQueryParams()));

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const updateFilters = useCallback(
		(newFilters: Partial<Filters>) => {
			setFilters((currentFilters) => {
				const mergedFilters = { ...currentFilters, ...newFilters };

				experienceTracker.start({
					name: SPACE_DIRECTORY_FILTER_EXPERIENCE,
					timeout: ExperienceTimeout.SPACE_DIRECTORY,
				});

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'filtered',
						actionSubject: 'spaceDirectoryContent',
						source: 'spaceDirectoryScreen',
						attributes: {
							spacesFilter: mergedFilters.spaceType,
							categoryFilter: !!mergedFilters.label,
							textSearch: !!mergedFilters.search,
						},
					},
				}).fire();

				// Setting query params triggers many re-renders in the app which is very slow
				// Defer this operation so the UI update is fast
				setTimeout(() => setQueryParams(filtersToQueryParams(mergedFilters), true), 0);

				return mergedFilters;
			});
		},
		[experienceTracker, createAnalyticsEvent, setQueryParams],
	);

	const updateSearch = useCallback((search: string) => updateFilters({ search }), [updateFilters]);

	const updateSpaceType = useCallback(
		(spaceType: string) => updateFilters({ spaceType }),
		[updateFilters],
	);

	const updateLabel = useCallback((label?: string) => updateFilters({ label }), [updateFilters]);

	const filtersContextValue = useMemo(
		() => ({
			filters,
			updateSearch,
			updateSpaceType,
			updateLabel,
		}),
		[filters, updateSearch, updateSpaceType, updateLabel],
	);

	return <FiltersContext.Provider value={filtersContextValue}>{children}</FiltersContext.Provider>;
};
